/** @file slot.fillMany: up to 256 slots filled (or emptied) in one command with one inverse (CR-012). */
import { deriveSlots } from '@zilo/pose3d-format/derive/deriveSlots';
import { failField } from '@zilo/pose3d-format/validate/fields/failField';
import type { CommandDef } from '../apply/commandDef';
import type { SlotFillItem } from '../types/puppetInputs';
import { compact } from '../validate/compact';
import { readInput } from '../validate/readInput';
import { fillTile } from './fillTile';
import { findSlot } from './findSlot';
import { previousFill } from './previousFill';
import { slotIndexOf } from './slotIndexOf';

/** The most slots one `slot.fillMany` takes. */
const MAX_FILLS = 256;

/** One item: `{ slot, image, fit? }` with `image` an image id or null (empty the slot). */
function readItem(raw: unknown, path: string): SlotFillItem {
  const r = readInput(raw, path, ['slot', 'image', 'fit']);
  return compact({ slot: r.str('slot'), image: r.nullable('image', r.str), fit: r.opt('fit', (k) => r.oneOf(k, ['pad', 'scale'] as const)) });
}

/**
 * `slot.fillMany { fills: [{ slot, image | null, fit? }] }` → `{ slots }`: 1..256 items, each slot once; each item
 * as `slot.fill` (size or `fit`, `IMAGE_RESIZED`), `null` empties the slot; all or none. One inverse:
 * `slot.fillMany { fills }` with every slot's previous image and fit (`null` where it was empty). Upload a labelled
 * image set with `images_upload` (names `<drawable>.<entry>.<tile>`) and fill it here.
 */
export const slotFillManyCommand: CommandDef<'slot.fillMany'> = {
  name: 'slot.fillMany',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => {
    const r = readInput(input, path, ['fills']);
    const raw = r.arr('fills');
    if (raw.length === 0 || raw.length > MAX_FILLS) failField(r.at('fills'), `1..${MAX_FILLS} items`, raw.length);
    const fills = raw.map((x, i) => readItem(x, `${r.at('fills')}/${i}`));
    const seen = new Set<string>();
    fills.forEach((x, i) => {
      if (seen.has(x.slot)) failField(`${r.at('fills')}/${i}/slot`, 'each slot once', x.slot);
      seen.add(x.slot);
    });
    return { fills };
  },
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const index = slotIndexOf(draft.puppet);
    const previous: SlotFillItem[] = [];
    const warnings = input.fills.flatMap((item, i) => {
      const located = index.get(item.slot) ?? findSlot(draft.puppet, item.slot, `/input/fills/${i}/slot`);
      previous.push(previousFill(draft, item.slot, located.entry.slots[String(located.slot.tile)]));
      return fillTile(draft, ctx, located, item, `/input/fills/${i}`);
    });
    const keys = new Set(input.fills.map((f) => f.slot));
    const slots = deriveSlots(draft.puppet).filter((s) => keys.has(s.key));
    return { result: { slots }, warnings, inverse: [{ name: 'slot.fillMany', input: { fills: previous } }] };
  },
};
